import { useEffect, useState } from 'react';
import axios from 'axios';
import styles from './CampoCarrinho.module.css';
import { CarrinhoItemValor } from '@tera/shared/src/lib/miniFormularioValidacao';
import { formatarMoeda, UNIDADE_PADRAO } from '@tera/shared/src/types/Catalogo';

type ItemCatalogo = {
    codigo: string;
    nome: string;
    descricao?: string;
    unidade?: string;
    preco: number;
};

type Props = {
    token: string;
    campoNome: string;
    maxItens?: number;
    value?: CarrinhoItemValor[];
    onChange: (itens: CarrinhoItemValor[]) => void;
    error?: string;
};

/**
 * Carrinho de itens. Os preços exibidos aqui vêm do proxy do backend e servem
 * só de referência visual: o total que vale é o que o servidor recalcula na
 * submissão, então o que sobe é apenas [{codigo, quantidade}].
 */
const CampoCarrinho = ({ token, campoNome, maxItens, value, onChange, error }: Props) => {
    const [itens, setItens] = useState<ItemCatalogo[]>([]);
    const [busca, setBusca] = useState('');
    const [carregando, setCarregando] = useState(true);
    const [falhou, setFalhou] = useState(false);

    const selecionados = value ?? [];

    useEffect(() => {
        let cancelado = false;
        setCarregando(true);
        const timer = setTimeout(async () => {
            try {
                const res = await axios.get(`/api/formulario-publico/${token}/catalogo`, {
                    params: { campo: campoNome, busca: busca || undefined },
                });
                if (cancelado) return;
                setItens(res.data?.itens ?? []);
                setFalhou(false);
            } catch {
                if (!cancelado) setFalhou(true);
            } finally {
                if (!cancelado) setCarregando(false);
            }
        }, 400);

        return () => {
            cancelado = true;
            clearTimeout(timer);
        };
    }, [token, campoNome, busca]);

    const quantidadeDe = (codigo: string) =>
        selecionados.find((s) => s.codigo === codigo)?.quantidade ?? 0;

    const definirQuantidade = (codigo: string, quantidade: number) => {
        const restantes = selecionados.filter((s) => s.codigo !== codigo);
        if (quantidade <= 0) return onChange(restantes);

        // Respeita o teto de linhas distintas configurado no campo.
        if (maxItens && restantes.length >= maxItens && !quantidadeDe(codigo)) return;

        onChange([...restantes, { codigo, quantidade }]);
    };

    // Um item já escolhido pode sair da lista ao filtrar; o resumo garante que
    // o cliente continue vendo (e conseguindo remover) tudo que selecionou.
    const nomePorCodigo = new Map(itens.map((i) => [i.codigo, i.nome]));

    return (
        <div className={styles.container}>
            <input
                className={styles.busca}
                type="text"
                placeholder="Buscar item..."
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
            />

            {carregando && <p className={styles.aviso}>Carregando itens...</p>}
            {falhou && <p className={styles.aviso}>Não foi possível carregar os itens.</p>}
            {!carregando && !falhou && !itens.length && (
                <p className={styles.aviso}>Nenhum item encontrado.</p>
            )}

            <ul className={styles.lista}>
                {itens.map((item) => {
                    const qtd = quantidadeDe(item.codigo);
                    return (
                        <li key={item.codigo} className={styles.item}>
                            <div className={styles.info}>
                                <span className={styles.nome}>{item.nome}</span>
                                <span className={styles.preco}>
                                    {formatarMoeda(item.preco)} / {item.unidade || UNIDADE_PADRAO}
                                </span>
                            </div>
                            <div className={styles.controles}>
                                <button
                                    type="button"
                                    className={styles.botaoQtd}
                                    onClick={() => definirQuantidade(item.codigo, qtd - 1)}
                                    disabled={qtd <= 0}
                                    aria-label={`Diminuir ${item.nome}`}
                                >
                                    −
                                </button>
                                <span className={styles.qtd}>{qtd}</span>
                                <button
                                    type="button"
                                    className={styles.botaoQtd}
                                    onClick={() => definirQuantidade(item.codigo, qtd + 1)}
                                    aria-label={`Aumentar ${item.nome}`}
                                >
                                    +
                                </button>
                            </div>
                        </li>
                    );
                })}
            </ul>

            {!!selecionados.length && (
                <div className={styles.resumo}>
                    <strong>Selecionados</strong>
                    {selecionados.map((s) => (
                        <div key={s.codigo} className={styles.resumoLinha}>
                            <span>
                                {s.quantidade}× {nomePorCodigo.get(s.codigo) ?? s.codigo}
                            </span>
                            <button
                                type="button"
                                className={styles.remover}
                                onClick={() => definirQuantidade(s.codigo, 0)}
                            >
                                remover
                            </button>
                        </div>
                    ))}
                    <p className={styles.aviso}>
                        O total é calculado ao enviar o formulário.
                    </p>
                </div>
            )}

            {error && <span className={styles.erro}>{error}</span>}
        </div>
    );
};

export default CampoCarrinho;
